/**
 * Session context for the Teacher AI Hub web app.
 *
 * Stores the active mock-login session in memory + sessionStorage so a page
 * refresh keeps you signed in for the demo run, but a tab close drops the
 * token (no real persistent auth in MVP).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ApiClient } from '../api/client';
import type { Outcome, UiSession, UiRole } from '../api/types';

const STORAGE_KEY = 'teacher-ai-hub.session.v1';

export interface SessionState {
  session: UiSession | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  signIn(input: { tenantId: string; role: UiRole; userId?: string }): Promise<
    Outcome<UiSession>
  >;
  signOut(): void;
}

const SessionContext = createContext<SessionState | null>(null);

export interface SessionProviderProps {
  client: ApiClient;
  children: ReactNode;
  /** Bypass storage hydration in tests so each test starts fresh. */
  initialSession?: UiSession | null;
}

export function SessionProvider({
  client,
  children,
  initialSession = null,
}: SessionProviderProps) {
  const [session, setSession] = useState<UiSession | null>(() => {
    if (initialSession !== null) return initialSession;
    return readStoredSession();
  });
  const [status, setStatus] = useState<SessionState['status']>('ready');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      writeStoredSession(session);
    } else {
      clearStoredSession();
    }
  }, [session]);

  const signIn = useCallback<SessionState['signIn']>(
    async (input) => {
      setStatus('loading');
      setError(null);
      const result = await client.mockLogin(input);
      if (!result.ok) {
        setStatus('error');
        setError(result.error.message);
        return result;
      }
      setSession(result.value);
      setStatus('ready');
      return result;
    },
    [client],
  );

  const signOut = useCallback(() => {
    setSession(null);
    setStatus('ready');
    setError(null);
  }, []);

  const value = useMemo<SessionState>(
    () => ({ session, status, error, signIn, signOut }),
    [session, status, error, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used inside a <SessionProvider>');
  }
  return ctx;
}

function readStoredSession(): UiSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UiSession;
    if (!parsed?.token || !parsed?.principal?.role) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredSession(session: UiSession): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // sessionStorage is best-effort; ignore quota / privacy-mode failures.
  }
}

function clearStoredSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
